import { Rank, Suit, type HkCard, type HkTableState } from "../gen/bazivan/v1/table_pb";
import { placeLabel } from "../game/place";
import { toLocal, toServer } from "../hokm/state";

export { toLocal, toServer };

/**
 * دیدِ محلیِ میزِ هفت‌خبیث — پورتِ HaftOnlineMap.
 *
 * همان دو کارِ نقشه‌ی حکم، با یک تفاوتِ مهم:
 *   • **چرخشِ صندلی:** سرور مطلق می‌شمارد، نما همیشه مرا صندلیِ ۰ می‌خواهد.
 *   • **جهت:** هفت‌خبیث پادساعت‌گرد هم می‌شود. چرخشِ صندلی جهت را عوض
 *     **نمی‌کند**، ولی هر جای دیگری که «نفرِ بعدی» را خودش حساب کند از سرور
 *     جدا می‌افتد. پس نمی‌کنیم: نوبت همیشه از سرور می‌آید.
 */
export type HaftLocal = {
  n: number; me: number;
  names: string[]; bots: boolean[]; avatars: string[]; subscribers: boolean[]; connected: boolean[]; playerIds: string[];
  /** برچسبِ جا (شهر یا کشور) — خالی اگر بازیکن نگفته یا نشان نداده. */
  places: string[];
  carpet: string; evil: boolean;
  myHand: HkCard[];
  counts: number[];            // به ایندکسِ محلی
  playable: boolean[];         // هم‌ایندکس با دستِ من
  top: HkCard | null;
  topSuit: Suit;
  turn: number; dir: 1 | -1;
  pendingDraw: number; sevenChain: number;
  awaitingSuit: boolean; awaitingTarget: boolean; awaitingBonus: boolean; awaitingOffer: boolean;
  bonusSuit: Suit; canPass: boolean;
  mirrored: boolean[];
  deckLeft: number;
  scores: number[];            // به ایندکسِ محلی — وگرنه جدولِ نتایج نامِ یکی را با امتیازِ دیگری نشان می‌دهد
  targetScore: number;
  roundOver: boolean; matchOver: boolean;
  winner: number; matchWinner: number;   // محلی؛ ‎-۱ = هنوز
  myTurn: boolean;
  /**
   * نمای تماشاچی: هیچ دستی مالِ من نیست.
   *
   * سرور my_hand را خالی می‌فرستد و me فقط لنگرِ چرخشِ صندلی‌هاست (۰). پس
   * صندلیِ ۰ هم مثلِ بقیه پشت‌ورو کشیده می‌شود و هیچ نوبتی «نوبتِ من» نیست —
   * وگرنه تماشاچی ساعتِ نوبت و دستِ خالیِ روشن می‌دید و فکر می‌کرد باید بازی
   * کند.
   */
  watching: boolean;

  turnRemaining: number;       // ثانیه؛ ۰ = بی‌مهلت (سرور باقی‌مانده می‌فرستد، نه ساعتِ پایان)
  warnOpen: boolean; warnLeft: number;
  catchOpen: boolean; catchSeat: number; catchLeft: number;
  offerFrom: number; offerCount: number;
};

export function emptyHaft(): HaftLocal {
  return {
    n: 4, me: 0, names: [], bots: [], avatars: [], subscribers: [], connected: [], playerIds: [], places: [],
    carpet: "", evil: true, myHand: [], counts: [], playable: [], top: null, topSuit: Suit.UNSPECIFIED,
    turn: -1, dir: 1, pendingDraw: 0, sevenChain: 0,
    awaitingSuit: false, awaitingTarget: false, awaitingBonus: false, awaitingOffer: false,
    bonusSuit: Suit.UNSPECIFIED, canPass: false, mirrored: [], deckLeft: 0, scores: [], targetScore: 0,
    roundOver: false, matchOver: false, winner: -1, matchWinner: -1, myTurn: false, turnRemaining: 0,
    watching: false,
    warnOpen: false, warnLeft: 0, catchOpen: false, catchSeat: -1, catchLeft: 0, offerFrom: -1, offerCount: 0,
  };
}

export function applyHaft(ts: HkTableState): HaftLocal {
  let n = ts.seats.length > 0 ? ts.seats.length : ts.handCounts.length;
  if (n <= 0) n = 4;
  const me = ts.me;
  const st = emptyHaft();
  st.n = n; st.me = me;
  st.names = new Array(n).fill(""); st.bots = new Array(n).fill(false); st.avatars = new Array(n).fill("");
  st.subscribers = new Array(n).fill(false); st.connected = new Array(n).fill(true); st.playerIds = new Array(n).fill(""); st.places = new Array(n).fill("");
  for (const s of ts.seats) {
    const l = toLocal(s.index, me, n);
    if (l < 0 || l >= n) continue;
    st.names[l] = s.name; st.bots[l] = s.bot; st.avatars[l] = s.avatar ?? "";
    st.subscribers[l] = !!s.subscriber; st.connected[l] = s.connected || s.bot; st.playerIds[l] = s.playerId ?? "";
    st.places[l] = placeLabel(s);
  }
  st.carpet = ts.carpet ?? ""; st.evil = ts.evil;
  st.watching = ts.watching;
  st.myHand = sortHk(ts.myHand);
  st.counts = new Array(n).fill(0);
  for (let l = 0; l < n; l++) {
    const server = toServer(l, me, n);
    st.counts[l] = l === 0 && !st.watching ? ts.myHand.length : (ts.handCounts[server] ?? 0);
  }
  st.playable = [...ts.myPlayable];
  st.top = ts.top ?? null;
  st.topSuit = ts.topSuit;
  st.turn = toLocal(ts.turn, me, n);
  st.dir = ts.dir >= 0 ? 1 : -1;
  st.pendingDraw = ts.pendingDraw; st.sevenChain = ts.sevenChain;
  st.awaitingSuit = ts.awaitingSuit; st.awaitingTarget = ts.awaitingTarget;
  st.awaitingBonus = ts.awaitingBonus; st.awaitingOffer = ts.awaitingOffer;
  st.bonusSuit = ts.bonusSuit; st.canPass = ts.canPass;
  st.mirrored = new Array(n).fill(false);
  for (let s = 0; s < ts.mirrored.length && s < n; s++) st.mirrored[toLocal(s, me, n)] = ts.mirrored[s]!;
  st.deckLeft = ts.deckLeft;
  st.scores = new Array(n).fill(0);
  for (let s = 0; s < ts.scores.length && s < n; s++) st.scores[toLocal(s, me, n)] = ts.scores[s]!;
  st.targetScore = ts.targetScore;
  st.roundOver = ts.roundOver; st.matchOver = ts.matchOver;
  st.winner = ts.winner >= 0 ? toLocal(ts.winner, me, n) : -1;
  st.matchWinner = ts.matchWinner >= 0 ? toLocal(ts.matchWinner, me, n) : -1;
  st.myTurn = !st.watching && st.turn === 0 && !st.roundOver;
  st.turnRemaining = ts.turnDeadlineMs / 1000;
  st.warnOpen = ts.warnOpen; st.warnLeft = ts.warnMsLeft / 1000;
  st.catchOpen = ts.catchOpen;
  st.catchSeat = ts.catchOpen && ts.catchSeat >= 0 ? toLocal(ts.catchSeat, me, n) : -1;
  st.catchLeft = ts.catchMsLeft / 1000;
  if (ts.awaitingOffer) { st.offerFrom = ts.offerFrom >= 0 ? toLocal(ts.offerFrom, me, n) : -1; st.offerCount = ts.offerCount; }
  return st;
}

/** نفرِ بعدی در جهتِ جاری — قربانیِ جریمه‌ی ۷ (فقط برای افکت؛ نوبت از سرور می‌آید). */
export const nextOf = (st: HaftLocal, seat: number) => ((seat + st.dir) % st.n + st.n) % st.n;

/**
 * مرتب‌کردنِ دستِ هفت‌خبیث.
 *
 * `sortHand` ِ مشترک به درد نمی‌خورد چون ورقِ این بازی `HkCard` است: یک
 * ورقِ معمولی به‌علاوه‌ی نشانِ «ویژه». ورق‌های ویژه آخر می‌نشینند و نه لای
 * خال‌ها — همان‌هایی‌اند که بازیکن موقعِ تصمیم دنبالشان می‌گردد، و قاطیِ
 * بقیه گم می‌شوند.
 */
function sortHk(hand: HkCard[]): HkCard[] {
  const order = [Suit.SPADES, Suit.HEARTS, Suit.CLUBS, Suit.DIAMONDS];
  const high = (r: number) => (r === Rank.ACE ? 14 : r);
  return [...hand].sort((a, b) => {
    const sa = a.special ? 1 : 0, sb = b.special ? 1 : 0;
    if (sa !== sb) return sa - sb;
    const oa = order.indexOf(a.card?.suit ?? Suit.UNSPECIFIED);
    const ob = order.indexOf(b.card?.suit ?? Suit.UNSPECIFIED);
    if (oa !== ob) return oa - ob;
    return high(b.card?.rank ?? 0) - high(a.card?.rank ?? 0);
  });
}
